import React from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import styles from "./CustomDatePicker.module.css";

// z-index higher than PopupModal overlay (9999)
const PICKER_Z_INDEX = 10001;

const CustomDatePicker = ({
  label,
  name,
  value,
  defaultValue,
  onChange,
  onBlur,
  placeholder = "Select date",
  disabled = false,
  required = false,
  readOnly = false,
  error,
  helperText,
  className = "",
  inputClassName = "",
  labelClassName = "",
  format = "DD/MM/YYYY",
  minDate,
  maxDate,
  excludeDates,
  includeDates,
  filterDate,
  disableWeekends = false,
  isClearable = true,
  showTimeSelect = false,
  size = "sm",
  ...rest
}) => {
  const sizeMap = {
    sm: "small",
    md: "medium",
    lg: "medium",
  };

  const getSxForSize = () => {
    if (size === "lg") {
      return {
        "& .MuiInputBase-input": {
          padding: "14px 16px",
          fontSize: "1.1rem",
        },
      };
    }
    if (size === "sm") {
      return {
        "& .MuiInputBase-input": {
          padding: "8px 12px",
          fontSize: "0.875rem",
        },
      };
    }
    return {};
  };

  const shouldDisableDate = (date) => {
    if (!date) return false;

    // Disable weekends
    if (disableWeekends) {
      const day = date.day();
      if (day === 0 || day === 6) return true;
    }

    // Custom filter function
    if (filterDate && !filterDate(date)) {
      return true;
    }

    // Exclude specific dates
    if (excludeDates) {
      return excludeDates.some((excludeDate) =>
        date.isSame(excludeDate, "day"),
      );
    }

    // Include only specific dates
    if (includeDates) {
      return !includeDates.some((includeDate) =>
        date.isSame(includeDate, "day"),
      );
    }

    return false;
  };

  const PickerComponent = showTimeSelect ? DateTimePicker : DatePicker;

  const commonSlotProps = {
    textField: {
      id: name,
      name: name,
      fullWidth: true,
      size: sizeMap[size],
      error: !!error,
      helperText: error || helperText,
      placeholder: placeholder,
      onBlur: onBlur,
      className: inputClassName,
      sx: {
        ...getSxForSize(),
        "& .MuiOutlinedInput-root": {
          borderRadius: "6px",
        },
        "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
          borderColor: "#ef4444",
        },
        "& .MuiFormHelperText-root.Mui-error": {
          color: "#ef4444",
        },
      },
    },
    field: {
      clearable: isClearable && !disabled && !readOnly,
    },
    actionBar: {
      actions: isClearable ? ["clear", "today", "accept"] : ["today", "accept"],
    },

    // --- Z-INDEX FIXES for modals / popups ---

    // Desktop view — calendar renders in a Popper (floating panel)
    popper: {
      sx: {
        zIndex: PICKER_Z_INDEX,
      },
    },
    // Desktop view — the Paper inside the Popper
    desktopPaper: {
      sx: {
        zIndex: PICKER_Z_INDEX,
      },
    },

    // Mobile view — calendar renders in a Dialog
    dialog: {
      sx: {
        zIndex: PICKER_Z_INDEX,
      },
    },
    mobilePaper: {
      sx: {
        zIndex: PICKER_Z_INDEX,
      },
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className={`${styles.datePickerWrapper} ${className}`}>
        {label && (
          <label
            htmlFor={name}
            className={`${styles.label} ${labelClassName} ${
              disabled ? styles.labelDisabled : ""
            }`}
          >
            {label}
            {required && <span className={styles.required}>*</span>}
          </label>
        )}

        <PickerComponent
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          disabled={disabled}
          readOnly={readOnly}
          minDate={minDate}
          maxDate={maxDate}
          format={showTimeSelect ? `${format} HH:mm` : format}
          shouldDisableDate={
            excludeDates || includeDates || filterDate || disableWeekends
              ? shouldDisableDate
              : undefined
          }
          slotProps={commonSlotProps}
          {...rest}
        />
      </div>
    </LocalizationProvider>
  );
};

export default CustomDatePicker;